import { Observable } from 'rxjs';
import { ofType, StateObservable } from 'redux-observable';
import {
  withLatestFrom,
  filter,
  concatMap,
  debounceTime,
} from 'rxjs/operators';
import { Action } from 'redux';
import { CreateCollaborator } from '../action-creators';
import AppState from '../../state';
import { UPDATE_FIELD } from '../../release/actions';
import { UpdateField } from '@webapp/store/release/action-creators';
import { ReleaseArtist } from '@common/types/release/release-artist';
import { trackList } from '../../tracks/selectors';
import { NewCollaborator } from '@services/collaborator/types/new-collaborator';
import { collaboratorsList } from '../selectors';

/**
 *
 * This effect creates collaborators after a successful Artist / Record Label on Release Edition
 *
 */

export const createCollaboratorsAfterEdition = (
  action$: Observable<Action>,
  state$: StateObservable<AppState>
) =>
  action$.pipe(
    ofType<Action, UpdateField<'artists'>>(UPDATE_FIELD),
    filter(
      (action) =>
        action.field === 'artists' && !!action.value && action.value.length > 1
    ),
    debounceTime(2000),
    withLatestFrom(state$),
    filter(([_, state]) => !!state.release.release?.id),
    concatMap(([action, state]) => {
      const tracks = trackList(state.tracks.tracks, state.release.release?.id);

      return tracks
        .filter((track) => !!track.id)
        .map((track) => {
          return getArtistAsCollaborators(
            track.id as string,
            action.value as ReleaseArtist[],
            state
          ).map((o) => new CreateCollaborator(o));
        })
        .flat();
    })
  );

const getArtistAsCollaborators = (
  assetId: string,
  artists: ReleaseArtist[],
  state: AppState
): NewCollaborator[] => {
  const collaborators = collaboratorsList(state.collaborator, assetId);

  const lastOrder = Math.max(0, ...collaborators.map((c) => c.order || 0));

  return artists
    .filter((artist) => {
      const exists = collaborators.find(
        (o) => o.collaboratorType === 'ARTIST' && o.collaboratorId === artist.id
      );
      const isCurrentArtist = state.artist.active?.id === artist.id;

      return !(exists || isCurrentArtist);
    })
    .map((artist, index) => {
      return {
        assetId,
        assetType: 'TRACK',
        type: 'COLLABORATOR',
        name: artist.name,
        contact: 'MISSING_CONTACT',
        contactType: 'email',
        collaboratorType: 'ARTIST',
        collaboratorId: artist.id,
        role: artist.role === 'owner' ? 'main' : artist.role,
        order: artist.role === 'owner' ? 0 : lastOrder + index + 1,
        releaseId: state.release.release?.id,
        platforms: artist.platforms,
      };
    });
};
